import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { useMarkAllNotificationsRead, useNotifications } from '../hooks/queries';
import type { AppNotification } from '../types';

const typeIcon: Record<AppNotification['type'], string> = {
  friend_request: '🤝',
  friend_accepted: '🎉',
  challenge_received: '⚔️',
  challenge_declined: '🙅',
  challenge_completed: '🏁',
  challenge_expired: '⌛',
};

const describe = (n: AppNotification): string => {
  const who = n.payload.actor_name ?? 'Someone';
  switch (n.type) {
    case 'friend_request':
      return `${who} sent you a friend request`;
    case 'friend_accepted':
      return `${who} accepted your friend request`;
    case 'challenge_received':
      return `${who} challenged you on ${n.payload.topic ?? 'a topic'}`;
    case 'challenge_declined':
      return `${who} declined your ${n.payload.topic ?? ''} challenge`;
    case 'challenge_completed':
      return n.payload.won === true
        ? `You beat ${who} on ${n.payload.topic ?? 'a topic'}! 🏆`
        : n.payload.won === false
          ? `${who} won your ${n.payload.topic ?? ''} challenge`
          : `Your challenge with ${who} ended in a tie`;
    case 'challenge_expired':
      return n.payload.won === true
        ? `${who} never played — you win by walkover`
        : `Your challenge with ${who} expired`;
  }
};

const linkFor = (n: AppNotification): string => {
  if (n.payload.challenge_id) return `/challenges/${n.payload.challenge_id}`;
  return '/friends';
};

const timeAgo = (iso: string): string => {
  // Backend timestamps are naive UTC — normalize before parsing
  const utc = iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`;
  const seconds = Math.max(0, (Date.now() - new Date(utc).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const prevUnread = useRef(0);

  const { data } = useNotifications(true);
  const markAllRead = useMarkAllNotificationsRead();
  const unread = data?.unread_count ?? 0;

  // New activity usually means challenge/friend state moved too
  useEffect(() => {
    if (unread > prevUnread.current) {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      queryClient.invalidateQueries({ queryKey: ['friend-requests'] });
      queryClient.invalidateQueries({ queryKey: ['friends'] });
    }
    prevUnread.current = unread;
  }, [unread, queryClient]);

  // Close on outside click (same pattern as the profile menu)
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markAllRead.mutate();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="glass relative rounded-full p-2 text-ink-soft transition-colors hover:text-ink"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-expanded={open}
      >
        <svg className="h-4.5 w-4.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          />
        </svg>
        <AnimatePresence>
          {unread > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -right-0.5 -top-0.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white"
            >
              {unread > 9 ? '9+' : unread}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="glass-menu absolute right-0 mt-2 w-80 overflow-hidden rounded-xl2 py-1.5"
          >
            <div className="border-b border-edge/60 px-4 py-2.5">
              <p className="text-sm font-semibold text-ink">Notifications</p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {(data?.notifications ?? []).length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-ink-faint">
                  Nothing yet — challenge a friend! ⚔️
                </p>
              ) : (
                data!.notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => {
                      setOpen(false);
                      navigate(linkFor(n));
                    }}
                    className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-accent-soft/40 ${
                      n.read ? '' : 'bg-accent-soft/25'
                    }`}
                  >
                    <span className="mt-0.5 text-lg" aria-hidden>
                      {typeIcon[n.type]}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm leading-snug text-ink">{describe(n)}</span>
                      <span className="mt-0.5 block text-xs text-ink-faint">
                        {timeAgo(n.created_at)}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
